import Highcharts from "highcharts";
import HighChartsMore from "highcharts/highcharts-more";
import HighChartsSeriesLabels from "highcharts/modules/series-label";
import React, { useMemo, useRef, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import HighChartsExport from "highcharts/modules/exporting";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
import PropTypes from "prop-types";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import theme from "theme";

HighChartsExport(Highcharts);
HighChartsMore(Highcharts);
HighChartsSeriesLabels(Highcharts);
HighchartsNoData(Highcharts);

const getChartOptions = newChartOptions => {
  // maintains the default chart options when overrides are not specifically set

  return {
    series: [],
    ...newChartOptions,
    // Add spacing to chart so that exports have padding
    chart: {
      zooming: { type: "xy" },
      spacingTop: parseInt(theme.spacing(3)),
      spacingRight: parseInt(theme.spacing(3)),
      spacingBottom: parseInt(theme.spacing(3)),
      spacingLeft: parseInt(theme.spacing(3)),
      // replace default chart options with new chart options if available
      ...newChartOptions?.chart
    },
    // get rid of the highchart logo
    credits: {
      enabled: false,
      ...newChartOptions?.credits
    },
    // Show only 2 decimal points on the graph
    tooltip: {
      valueDecimals: 2,
      ...newChartOptions?.tooltip
    },
    title: {
      // Wrapp the title when screen is resizeed
      widthAdjust: -200,
      ...newChartOptions?.title
    },
    // Remove exporting hamburger and default export resolution
    exporting: {
      enabled: false,
      sourceWidth: 1200,
      sourceHeight: 800,
      scale: 1,
      ...newChartOptions?.exporting
    }
  };
};

/**
 * A component which displays a Highchart depending on the options provided.
 * Has some default settings
 * @param chartOptions the Highchart options. {@link https://api.highcharts.com/highcharts/ see highcharts api}
 * @returns {JSX.Element} a chart component
 */
export const Chart = ({ chartOptions, chartDropdowns }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const ref = useRef();
  function openExportMenu(event) {
    setAnchorEl(event.currentTarget);
  }

  function closeExportMenu() {
    setAnchorEl(false);
  }

  function handleExport(fileType) {
    ref.current.chart.exportChart({
      type: fileType
    });
  }

  const updateChartOptions = useMemo(() => {
    return getChartOptions(chartOptions);
  }, [chartOptions]);

  return (
    <ChartWrapper>
      <ChartOverlay>
        {chartDropdowns}
        <Tooltip title="More Options" placement="top">
          <IconButton
            aria-label="Chart Options"
            onClick={openExportMenu}
            size="large"
          >
            <MoreHoriz />
          </IconButton>
        </Tooltip>
        <ExportMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          onClose={closeExportMenu}
        >
          <MenuItem
            onClick={() => handleExport("application/pdf")}
            data-qa="CP-Button-Export-PDF"
          >
            Export (PDF)
          </MenuItem>
          <MenuItem
            onClick={() => handleExport("image/png")}
            data-qa="CP-Button-Export-PNG"
          >
            Export (PNG)
          </MenuItem>
          <MenuItem
            onClick={() => handleExport("image/svg+xml")}
            data-qa="CP-Button-Export-SVG"
          >
            Export (SVG)
          </MenuItem>
        </ExportMenu>
      </ChartOverlay>
      <HighchartsReact
        id="container"
        highcharts={Highcharts}
        options={updateChartOptions}
        immutable={true}
        ref={ref}
      />
    </ChartWrapper>
  );
};

Chart.propTypes = {
  chartOptions: PropTypes.object,
  chartDropdowns: PropTypes.object
};

const ExportMenu = styled(Menu)`
  margin-top: ${props => props.theme.spacing(1)};
`;

const ChartOverlay = styled("div")`
  top: ${props => props.theme.spacing(1)};
  right: ${props => props.theme.spacing(1)};
  position: absolute;
  z-index: 1;
  display: flex;
  gap: ${props => props.theme.spacing(1)};
`;

const ChartWrapper = styled("div")`
  position: relative;
`;
